import { Link } from 'react-router-dom';
import { m } from '../../lib/motion.js';
import { imageProps, lqipStyle, cn } from '../../lib/format.js';

/**
 * The home banner.
 *
 * A warm, dark band with the store's own words on the left and a shelf of real
 * bottles on the right. There is no static artwork: the tiles are whatever an
 * operator has marked as featured, so the banner always shows goods that can be
 * bought today, and a fresh catalogue degrades to the type column alone rather
 * than to a hole in the layout.
 *
 * Every word is a setting — kicker, headline, paragraph — so the largest copy on
 * the site changes without a deploy.
 *
 * LCP discipline: the headline rises but never fades from zero, and the first
 * tiles load eagerly. Nothing the browser might report as the largest paint
 * depends on an animation finishing to be visible.
 */

const stagger = (index, reduced) => ({
  initial: reduced ? { opacity: 0 } : { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: {
    duration: reduced ? 0.2 : 0.4,
    delay: reduced ? 0 : index * 0.07,
    ease: [0.25, 1, 0.5, 1],
  },
});

const rise = (reduced) => ({
  initial: reduced ? false : { y: 14 },
  animate: { y: 0 },
  transition: { duration: reduced ? 0 : 0.4, delay: reduced ? 0 : 0.07, ease: [0.25, 1, 0.5, 1] },
});

// Tiles past the third only appear once the column is wide enough to hold them
// at a size where a bottle still reads as a bottle.
const TILE_VISIBILITY = ['', '', '', 'hidden lg:block', 'hidden xl:block'];

export const HomeHero = ({ copy = {}, products, isPending, reduced }) => {
  const headingLines = Array.isArray(copy.heroHeading)
    ? copy.heroHeading
    : String(copy.heroHeading ?? '').split('\n').filter(Boolean);

  const tiles = (products ?? []).filter((product) => product.images?.[0]).slice(0, 5);
  const showShelf = isPending || tiles.length > 0;

  return (
    <section
      aria-label="Welcome"
      className="relative isolate overflow-hidden bg-[linear-gradient(120deg,#1f1811_0%,#2b2118_38%,#5a4128_100%)] text-white"
    >
      {/* A low gold glow behind the shelf, so the bottles sit in light rather
          than on a flat fill. Decorative. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(60%_70%_at_78%_62%,rgba(212,170,98,0.28),transparent_70%)]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-1/3 bg-[linear-gradient(to_top,rgba(0,0,0,0.35),transparent)]"
      />

      <div
        className={cn(
          'mx-auto grid max-w-(--container-content) items-center gap-10 px-4 py-14 sm:px-6 sm:py-16 lg:min-h-[560px] lg:gap-12 lg:py-20',
          showShelf && 'lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]',
        )}
      >
        <div className="max-w-xl">
          {copy.heroKicker && (
            <m.p
              {...stagger(0, reduced)}
              className="flex items-center gap-3 text-2xs font-semibold tracking-[0.2em] text-[#d9b97a] uppercase"
            >
              <span aria-hidden="true" className="inline-block h-px w-8 bg-[#d9b97a]" />
              {copy.heroKicker}
            </m.p>
          )}

          {headingLines.length > 0 && (
            <m.h1
              {...rise(reduced)}
              className="mt-5 font-display text-[2.25rem] leading-[1.04] font-medium tracking-[0.01em] text-balance uppercase sm:text-5xl lg:text-[3.4rem]"
            >
              {headingLines.map((line, index) => (
                <span
                  key={line}
                  className={cn('block', index === headingLines.length - 1 && headingLines.length > 1 && 'text-[#d9b97a]')}
                >
                  {line}
                </span>
              ))}
            </m.h1>
          )}

          {copy.heroLede && (
            <m.p {...stagger(2, reduced)} className="mt-5 max-w-md text-sm leading-relaxed text-white/75">
              {copy.heroLede}
            </m.p>
          )}

          <m.div {...stagger(3, reduced)} className="mt-8">
            <Link
              to="/shop?world=fragrance"
              className="inline-flex items-center bg-brand px-7 py-3.5 text-2xs font-semibold tracking-[0.12em] text-white uppercase transition-colors hover:bg-brand-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              Shop now
            </Link>
          </m.div>
        </div>

        {showShelf && (
          <div className="relative">
            <ul className="grid grid-cols-3 items-end gap-2.5 sm:gap-3 lg:grid-cols-4 xl:grid-cols-5">
              {isPending
                ? Array.from({ length: 5 }, (_, index) => (
                    <li key={index} aria-hidden="true" className={TILE_VISIBILITY[index]}>
                      <div className="aspect-[3/4] w-full bg-white/8 motion-safe:animate-pulse" />
                    </li>
                  ))
                : tiles.map((product, index) => (
                    <li key={product._id} className={TILE_VISIBILITY[index]}>
                      <Link
                        to={`/product/${product.slug}`}
                        className="group block focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#d9b97a]"
                      >
                        <span
                          className="relative block aspect-[3/4] overflow-hidden bg-cream shadow-[0_18px_40px_-18px_rgba(0,0,0,0.7)]"
                          style={lqipStyle(product.images[0])}
                        >
                          <img
                            {...imageProps(product.images[0], {
                              sizes: '(min-width: 1280px) 11vw, (min-width: 1024px) 12vw, 30vw',
                              eager: index < 3,
                            })}
                            alt={product.images[0].alt || product.title}
                            className="absolute inset-0 size-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                          />
                        </span>
                        <span className="mt-2 block truncate text-center text-[11px] tracking-[0.06em] text-white/70 uppercase group-hover:text-white">
                          {product.title}
                        </span>
                      </Link>
                    </li>
                  ))}
            </ul>
            {/* The shelf the bottles stand on. */}
            <div aria-hidden="true" className="mt-3 h-px bg-[linear-gradient(to_right,transparent,rgba(217,185,122,0.6),transparent)]" />
          </div>
        )}
      </div>
    </section>
  );
};

export default HomeHero;
